import { CoachIsEnumType } from 'components/molecules/CoachSelector';
import { HealthGoalTypesType } from 'components/molecules/HealthGoalSelection';
import { DashboardActionType } from 'store/dashboard/dashboard.actionCreators';
import * as dashboardAT from 'store/dashboard/dashboard.actionTypes';
import { DateType } from 'types/Date';
import { GenderKeyType } from 'types/GenderKey';
import { AllergyType, NutritionPhilosophyType } from '../../types/DietaryHabits';
import { ProfileActionType } from './profile.actionCreators';
import {
	CHANGE_ANAMNESIS_DISCLAIMER_ACCEPTED,
	REQUEST_FINISH_ANAMNESIS,
	REQUEST_FINISH_ANAMNESIS_FAILURE,
	REQUEST_FINISH_ANAMNESIS_SUCCESS,
	REQUEST_FINISH_ONBOARDING_FAILURE,
	REQUEST_FINISH_ONBOARDING_LOADING,
	REQUEST_FINISH_ONBOARDING_SUCCESS,
	REQUEST_SAVE_ACCEPT_HEALTH_TARGET,
	REQUEST_SAVE_ACCEPT_HEALTH_TARGET_FAILURE,
	REQUEST_SAVE_ACCEPT_HEALTH_TARGET_SUCCESS,
	REQUEST_SAVE_ACTIVITY_LEVEL,
	REQUEST_SAVE_ACTIVITY_LEVEL_FAILURE,
	REQUEST_SAVE_ACTIVITY_LEVEL_SUCCESS,
	REQUEST_SAVE_AGE,
	REQUEST_SAVE_AGE_FAILURE,
	REQUEST_SAVE_AGE_SUCCESS,
	REQUEST_SAVE_COACH,
	REQUEST_SAVE_COACH_FAILURE,
	REQUEST_SAVE_COACH_SUCCESS,
	REQUEST_SAVE_DIETARY_HABITS,
	REQUEST_SAVE_DIETARY_HABITS_FAILURE,
	REQUEST_SAVE_DIETARY_HABITS_SUCCESS,
	REQUEST_SAVE_DSGVO,
	REQUEST_SAVE_DSGVO_FAILURE,
	REQUEST_SAVE_DSGVO_SUCCESS,
	REQUEST_SAVE_GENDER,
	REQUEST_SAVE_GENDER_FAILURE,
	REQUEST_SAVE_GENDER_SUCCESS,
	REQUEST_SAVE_HEALTH_TARGET,
	REQUEST_SAVE_HEALTH_TARGET_SUCCESS,
	REQUEST_SAVE_HEIGHT,
	REQUEST_SAVE_HEIGHT_FAILURE,
	REQUEST_SAVE_HEIGHT_SUCCESS,
	REQUEST_SAVE_PARTICIPATE_COMPETITION,
	REQUEST_SAVE_SLOGAN,
	REQUEST_SAVE_SLOGAN_FAILURE,
	REQUEST_SAVE_SLOGAN_SUCCESS,
	REQUEST_SAVE_WEIGHT,
	REQUEST_SAVE_WEIGHT_FAILURE,
	REQUEST_SAVE_WEIGHT_SUCCESS,
	REQUEST_USER_DATA_FAILURE,
	REQUEST_USER_DATA_LOADING,
	REQUEST_USER_DATA_SUCCESS,
} from './profile.actionTypes';

export type ProfileDataType = {
	tempPregnant: null | boolean;
	tempAcceptedPregnancyWarning: null | boolean;
	acceptedAgeWarning: boolean;
	acceptedAnamnesisWarning: boolean;
	acceptedHealthGoalWarningOverweight: boolean;
	acceptedHealthGoalWarningPregnant: boolean;
	acceptedHealthGoalWarningUnderweight: boolean;
	acceptedHeightWarning: boolean;
	acceptedPregnancyWarning: boolean;
	acceptedWeightWarning: boolean;
	anamnesisDone: boolean;
	birthdate: DateType;
	bmi: number;
	bodyFat: number;
	coach: CoachIsEnumType;
	dsgvo: 'TRUE' | 'FALSE' | 'UNKNOWN';
	showDsgvoReminder: boolean;
	desiredWeight: number;
	email: string;
	firstName: string;
	creationDate: {
		date: string;
		time: string;
		year: number;
	};
	gender: GenderKeyType;
	healthGoal: HealthGoalTypesType;
	tempHealthGoal: HealthGoalTypesType;
	height: number;
	intensity: number;
	lastName: string;
	level: string;
	maxWeight: number;
	minWeight: number;
	personId: string;
	pregnant: null | boolean;
	previewUser: boolean;
	slogan: string;
	isUpdatingSlogan: boolean;
	suggestedWeightReduceGoal: number;
	waist: number;
	weight: number;
	weightReduceGoal: number;
	message: string;
	participateCompetition: boolean;
	nutrition: 'vegan' | 'vegetarian';
	nutritionPhilosophy: NutritionPhilosophyType;
	allergies: AllergyType[];
	points: string | null;
	hasNewsletterSubscription: boolean;
};

export type ProfileStateType = {
	error: null | boolean;
	finished: null | boolean;
	userData: null | ProfileDataType;
	userDataLoaded: boolean;
	isLoadingUserData: boolean;
	isLoadingFinishOnboarding: boolean;
	isUpdatingAnamnesis: boolean;
	waitForValues: {
		savedCoach: null | boolean;
		savedGender: null | boolean;
		savedAge: null | boolean;
		savedHeight: null | boolean;
		savedWeight: null | boolean;
		savedActivityLevel: null | boolean;
		savedDietaryHabits: null | boolean;
		savedDsgvo: null | boolean;
		verified: null | boolean;
		savedFinished: false | boolean;
	};
};

export type RootStateType = {
	profile: ProfileStateType;
};

// Reducer
// ------------------------------------

export const initialState: ProfileStateType = {
	error: null,
	finished: null,
	userData: null,
	userDataLoaded: false,
	isLoadingUserData: false,
	isLoadingFinishOnboarding: false,
	isUpdatingAnamnesis: false,
	waitForValues: {
		savedCoach: null,
		savedGender: null,
		savedAge: null,
		savedHeight: null,
		savedWeight: null,
		savedActivityLevel: null,
		savedDietaryHabits: null,
		savedDsgvo: null,
		verified: null,
		savedFinished: false,
	},
};

const getActionError = (action: any /*ProfileActionType*/) => {
	try {
		if (action && action.error && action.error.response) {
			return getActionError(action);
		}
	} catch (exception) {
		// do nothing
	}

	return new Error('Unknown Error');
};

export default (state: ProfileStateType = initialState, action: ProfileActionType | DashboardActionType) => {
	switch (action.type) {
		case REQUEST_SAVE_COACH:
		case REQUEST_SAVE_GENDER:
		case REQUEST_SAVE_AGE:
		case REQUEST_SAVE_HEIGHT:
		case REQUEST_SAVE_WEIGHT:
		case REQUEST_SAVE_ACCEPT_HEALTH_TARGET:
		case REQUEST_SAVE_HEALTH_TARGET:
		case REQUEST_SAVE_DIETARY_HABITS:
		case REQUEST_SAVE_ACTIVITY_LEVEL:
		case REQUEST_SAVE_DSGVO:
		case REQUEST_FINISH_ANAMNESIS:
			return {
				...state,
				isUpdatingAnamnesis: true,
			};
		case REQUEST_SAVE_HEALTH_TARGET_SUCCESS:
			return {
				...state,
				userData: {
					...action.data,
				},
				waitForValues: {
					...state.waitForValues,
					savedHealthTarget: true,
				},
				isUpdatingAnamnesis: false,
			};

		case CHANGE_ANAMNESIS_DISCLAIMER_ACCEPTED:
			return {
				...state,
				anamnesisDisclaimerAccepted: action.anamnesisDisclaimerAccepted,
			};

		case REQUEST_SAVE_PARTICIPATE_COMPETITION:
			return {
				...state,
				userData: { ...state.userData, participateCompetition: action.isParticipating },
			};

		case dashboardAT.REQUEST_SAVE_DASHBOARD_MEASUREMENT_SUCCESS:
			return {
				...state,
				userData: {
					...state.userData,
					weight: (action as any).measurement.weight || (state as any).userData.weight,
				},
			};

		case REQUEST_USER_DATA_LOADING:
			return {
				...state,
				isLoadingUserData: action.status,
			};

		case REQUEST_USER_DATA_SUCCESS:
			return {
				...state,
				userData: action.data,
				isLoadingUserData: false,
				userDataLoaded: true,
			};

		case REQUEST_SAVE_COACH_SUCCESS:
			return {
				...state,
				userData: action.data,
				waitForValues: {
					savedCoach: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_GENDER_SUCCESS:
			return {
				...state,
				userData: action.data,
				waitForValues: {
					...state.waitForValues,
					savedGender: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_AGE_SUCCESS:
			return {
				...state,
				userData: action.data,
				waitForValues: {
					...state.waitForValues,
					savedAge: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_HEIGHT_SUCCESS:
			return {
				...state,
				userData: action.data,
				waitForValues: {
					...state.waitForValues,
					savedHeight: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_WEIGHT_SUCCESS:
			return {
				...state,
				userData: action.data,
				waitForValues: {
					...state.waitForValues,
					savedWeight: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_ACCEPT_HEALTH_TARGET_SUCCESS:
			return {
				...state,
				userData: {
					...action.data,
					tempHealthGoal: action.data.healthGoal,
				},
				waitForValues: {
					...state.waitForValues,
					savedAcceptHealthTarget: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_ACTIVITY_LEVEL_SUCCESS:
			return {
				...state,
				userData: action.data,
				waitForValues: {
					...state.waitForValues,
					savedActivityLevel: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_DSGVO_SUCCESS:
			return {
				...state,
				userData: action.data,
				waitForValues: {
					...state.waitForValues,
					savedDsgvo: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_DIETARY_HABITS_SUCCESS:
			return {
				...state,
				userData: action.data,
				waitForValues: {
					...state.waitForValues,
					savedDietaryHabits: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_FINISH_ANAMNESIS_SUCCESS:
			return {
				...state,
				userData: action.data,
				anamnesisDisclaimerAccepted: true,
				waitForValues: {
					...state.waitForValues,
					savedFinished: true,
				},
				isUpdatingAnamnesis: false,
			};

		case REQUEST_SAVE_SLOGAN:
			return {
				...state,
				userData: {
					...state.userData,
					slogan: action.slogan,
					isUpdatingSlogan: true,
				},
			};

		case REQUEST_SAVE_SLOGAN_SUCCESS:
			return {
				...state,
				userData: {
					...state.userData,
					slogan: action.slogan,
					isUpdatingSlogan: false,
				},
			};

		case REQUEST_SAVE_SLOGAN_FAILURE:
			return {
				...state,
				error: getActionError(action),
				userData: {
					...state.userData,
					isUpdatingSlogan: false,
				},
			};

		case REQUEST_USER_DATA_FAILURE:
		case REQUEST_FINISH_ONBOARDING_SUCCESS:
			return {
				...state,
				userData: action.userData,
				waitForValues: {
					...state.waitForValues,
					savedOnboarding: true,
				},
			};

		case REQUEST_FINISH_ONBOARDING_LOADING:
			return {
				...state,
				isLoadingFinishOnboarding: action.status,
			};

		case REQUEST_FINISH_ANAMNESIS_FAILURE:
		case REQUEST_SAVE_COACH_FAILURE:
		case REQUEST_SAVE_GENDER_FAILURE:
		case REQUEST_SAVE_AGE_FAILURE:
		case REQUEST_SAVE_HEIGHT_FAILURE:
		case REQUEST_SAVE_WEIGHT_FAILURE:
		case REQUEST_SAVE_ACCEPT_HEALTH_TARGET_FAILURE:
		case REQUEST_SAVE_ACTIVITY_LEVEL_FAILURE:
		case REQUEST_SAVE_DIETARY_HABITS_FAILURE:
		case REQUEST_SAVE_DSGVO_FAILURE:
		case REQUEST_FINISH_ONBOARDING_FAILURE:
			return {
				...state,
				error: getActionError(action),
				isUpdatingAnamnesis: false,
			};
		default:
			return state;
	}
};

// Action Creators
// ------------------------------------
